
import { useEffect, useState } from "react";
import { RotateCcw, X } from "lucide-react";
import { useIsMobile, useIsLandscape } from "@/lib/useMediaQuery";
import { useUiStore } from "../stores/uiStore";
import { cn } from "@/lib/utils";

/** Auto-dismiss delay for the orientation hint (ms) */
const AUTO_DISMISS_DELAY = 6000;

/**
 * Subtle toast that suggests rotating to landscape for a better 3D experience.
 *
 * Behavior:
 * - Only shows on mobile in portrait orientation
 * - Only shows once per user (persisted via localStorage)
 * - Auto-dismisses after 6 seconds or on tap
 */
export function OrientationHint() {
  const isMobile = useIsMobile();
  const isLandscape = useIsLandscape();
  const hasSeenHint = useUiStore((s) => s.hasSeenOrientationHint);
  const dismissOrientationHint = useUiStore((s) => s.dismissOrientationHint);

  const [visible, setVisible] = useState(false);

  // Show the hint only when: mobile, portrait, and user hasn't dismissed it
  const shouldShow = isMobile && !isLandscape && !hasSeenHint;

  useEffect(() => {
    if (!shouldShow) {
      setVisible(false);
      return;
    }

    // Small delay before showing to avoid flashing on quick page transitions
    const showTimer = window.setTimeout(() => setVisible(true), 500);

    // Auto-dismiss after a few seconds
    const hideTimer = window.setTimeout(() => {
      setVisible(false);
      dismissOrientationHint();
    }, AUTO_DISMISS_DELAY);

    return () => {
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
    };
  }, [shouldShow, dismissOrientationHint]);

  // Don't render anything if we shouldn't show the hint
  if (!shouldShow) return null;

  return (
    <div
      className={cn(
        "fixed bottom-20 left-1/2 -translate-x-1/2 z-40",
        "flex items-center gap-2 px-4 py-2.5",
        "bg-sands-800/90 backdrop-blur-sm text-white rounded-full shadow-lg",
        "transition-all duration-300 ease-out",
        visible
          ? "opacity-100 translate-y-0"
          : "opacity-0 translate-y-2 pointer-events-none",
      )}
      onClick={() => {
        setVisible(false);
        dismissOrientationHint();
      }}
    >
      <RotateCcw className="w-4 h-4 shrink-0" />
      <span className="text-xs font-medium whitespace-nowrap">
        Rotate for a wider view
      </span>
      <button
        onClick={(e) => {
          e.stopPropagation();
          setVisible(false);
          dismissOrientationHint();
        }}
        className="min-w-[28px] min-h-[28px] flex items-center justify-center rounded-full hover:bg-white/10 transition-colors"
        title="Dismiss"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
